import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Save } from 'lucide-react';
import { extraProductsApi } from '@/services/extraProductsApi';
import { uploadImageToStorageV2 } from '@/utils/storage';
import toast from 'react-hot-toast';

export const ExtraProductForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    price: '',
    status: 'active' as 'active' | 'inactive',
  });

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);

  useEffect(() => {
    if (isEditMode && id) {
      loadExtraProduct();
    }
  }, [id, isEditMode]);

  const loadExtraProduct = async () => {
    setLoadingProduct(true);
    try {
      const product = await extraProductsApi.getExtraProduct(id!);
      setFormData({
        title: product.title,
        description: product.description || '',
        imageUrl: product.imageUrl || '',
        price: product.price.toString(),
        status: product.status,
      });
    } catch (error) {
      console.error('Ürün yüklenemedi:', error);
      toast.error('Ürün yüklenemedi');
      navigate('/admin/extra-products');
    } finally {
      setLoadingProduct(false);
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu en fazla 5MB olabilir');
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('Sadece resim dosyaları yüklenebilir');
      return;
    }

    setUploading(true);
    try {
      const downloadURL = await uploadImageToStorageV2(file, `extra-products/${Date.now()}`, 'extra-product');
      setFormData({ ...formData, imageUrl: downloadURL });
      toast.success('Görsel yüklendi');
    } catch (error) {
      console.error('Görsel yüklenemedi:', error);
      toast.error('Görsel yüklenemedi');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title || !formData.price) {
      toast.error('Ürün adı ve fiyat zorunludur');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        price: parseFloat(formData.price),
      };

      if (isEditMode && id) {
        await extraProductsApi.updateExtraProduct(id, data);
        toast.success('Ekstra ürün güncellendi');
      } else {
        await extraProductsApi.createExtraProduct(data);
        toast.success('Ekstra ürün oluşturuldu');
      }

      navigate('/admin/extra-products');
    } catch (error) {
      console.error('Kaydetme hatası:', error);
      toast.error(isEditMode ? 'Ürün güncellenemedi' : 'Ürün oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  if (loadingProduct) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin text-4xl">⏳</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/admin/extra-products')}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-smooth"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-dark dark:text-light">
            {isEditMode ? 'Ekstra Ürünü Düzenle' : 'Yeni Ekstra Ürün'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isEditMode ? 'Ekstra ürün bilgilerini güncelleyin' : 'Yeni bir ekstra ürün ekleyin'}
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="max-w-3xl">
        <div className="card space-y-6">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ürün Adı *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="input-field"
              placeholder="Örn: Hediye Paketi, Balon, Çikolata"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Açıklama
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="input-field resize-none"
              rows={4}
              placeholder="Ürün açıklaması..."
            />
          </div>

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ürün Görseli
            </label>
            {formData.imageUrl && (
              <div className="mb-4">
                <img
                  src={formData.imageUrl}
                  alt={formData.title}
                  className="w-48 h-48 object-cover rounded-lg border-2 border-gray-200 dark:border-gray-700"
                />
              </div>
            )}
            <div>
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                disabled={uploading}
              />
              <label
                htmlFor="image-upload"
                className={`btn-secondary inline-flex items-center gap-2 cursor-pointer ${
                  uploading ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Upload size={18} />
                {uploading ? 'Yükleniyor...' : formData.imageUrl ? 'Görseli Değiştir' : 'Görsel Yükle'}
              </label>
              <p className="text-xs text-gray-500 mt-2">
                Max 5MB. JPG, PNG, WebP veya HEIC secilebilir; HEIC otomatik JPEG'e cevrilir
              </p>
            </div>
          </div>

          {/* Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Fiyat (₺) *
            </label>
            <input
              type="number"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: e.target.value })}
              className="input-field"
              placeholder="0.00"
              step="0.01"
              min="0"
              required
            />
          </div>

          {/* Status */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Durum
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as 'active' | 'inactive' })}
              className="input-field"
            >
              <option value="active">Aktif</option>
              <option value="inactive">Pasif</option>
            </select>
            <p className="text-xs text-gray-500 mt-2">
              Pasif ürünler müşterilere gösterilmez
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary flex items-center gap-2"
            >
              <Save size={18} />
              {loading ? 'Kaydediliyor...' : isEditMode ? 'Güncelle' : 'Oluştur'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/extra-products')}
              className="btn-secondary"
            >
              İptal
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
